import * as React from 'react';
import { useState } from 'react';
import { Icon as LegacyIcon } from '@ant-design/compatible';
import { message } from 'antd';
import OWUpload from '../OWUpload';
import { imageUploadValidation } from '../../utils/ImageUtils';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import { getProtectedImageUriGeneric } from '../../utils/FileAccessUtils';

/**
 * Derives the displayable image URL from an antd Upload `info` after an OWUpload
 * (presigned-S3) request completes. The presigned endpoint uploads to the private
 * bucket and returns `{ fileId, fileName, presignedUrl }`, so the display URL must
 * be built from fileId/fileName via the images CDN — the raw presigned PUT URL is
 * not publicly readable. The fileUrl/url/presignedUrl branches are fallbacks for
 * any endpoint that returns a ready-to-use URL directly.
 */
export const getUploadedImageUrl = (info: any): string => {
	const fileId = nullSafeGet('file.response.data.fileId', info);
	const fileName = nullSafeGet('file.response.data.fileName', info);
	if (fileId && fileName) {
		return getProtectedImageUriGeneric(fileId, fileName);
	}
	const presignedUrl = nullSafeGet('file.response.data.presignedUrl', info);
	return (
		nullSafeGet('file.response.data.fileUrl', info) ||
		nullSafeGet('file.response.data.url', info) ||
		(presignedUrl ? presignedUrl.split('?')[0] : null)
	);
};

interface OWLogoUploadProps {
	/** Currently-saved image URL to preview; falsy shows the upload placeholder. */
	imageUrl: string;
	/** Called with the uploaded image's public URL once an upload succeeds. */
	onChange: (url: string) => void;
	/** Upload-tile class. Defaults to the shared `avatar-uploader` styling. */
	className?: string;
	/** Placeholder label shown in the empty upload tile. */
	uploadText?: string;
}

/**
 * Picture-card logo uploader built on OWUpload (presigned-S3 direct upload).
 * Owns its loading spinner and surfaces only the final URL via `onChange`, so
 * callers don't repeat the upload-status / URL-extraction boilerplate. OWUpload
 * already toasts on a failed request, so this only clears the spinner on error.
 */
export const OWLogoUpload = (props: OWLogoUploadProps) => {
	const { imageUrl, onChange, className = 'avatar-uploader', uploadText = 'Upload' } = props;
	const [uploadLoading, setUploadLoading] = useState(false);

	const handleUploadChange = (info) => {
		if (info.file.status === 'uploading') {
			setUploadLoading(true);
			return;
		}
		if (info.file.status === 'done') {
			setUploadLoading(false);
			const uploadedUrl = getUploadedImageUrl(info);
			if (uploadedUrl) {
				onChange(uploadedUrl);
			} else {
				message.error('Failed to upload image. Please try again.');
			}
			return;
		}
		if (info.file.status === 'error') {
			setUploadLoading(false);
		}
	};

	return (
		<OWUpload
			name="file"
			listType="picture-card"
			className={className}
			showUploadList={false}
			beforeUpload={imageUploadValidation}
			onChange={handleUploadChange}
		>
			{imageUrl ? (
				<img src={imageUrl} style={{ maxHeight: 104, maxWidth: '100%' }} alt="" />
			) : (
				<div>
					<LegacyIcon type={uploadLoading ? 'loading' : 'plus'} />
					<div className="ant-upload-text">{uploadText}</div>
				</div>
			)}
		</OWUpload>
	);
};
